import {typedMemo} from "../../../utils/typescript";
import {SelectionCheckbox} from "./SelectionCheckbox";
import {ColumnHeader} from "./ColumnHeader";
import {useCallback} from "react";
import {
  ColumnId,
  ExtractTableItemKey,
  SetGroupStateFunction,
  SetSelectedRowKeysFunction,
  SetSortStateFunction,
  SortState,
  TableConfig,
} from "../../../hooks/table";
import {BaseTableColumnHeader, BaseTableRow} from "./base";
import {TableSize} from ".";

export const HeaderContent = typedMemo(
  <C extends TableConfig>({
    config,
    columnIds,
    selectableRowKeys,
    selectedRowKeys,
    sortState,
    totalFractionalWidth,
    setGroupState,
    setSortState,
    setSelectedRowKeys,
    supportsSelection,
    size,
  }: {
    config: C;
    columnIds: ColumnId<C>[];
    selectableRowKeys: Set<ExtractTableItemKey<C>>;
    selectedRowKeys: Set<ExtractTableItemKey<C>>;
    sortState: SortState<C>;
    totalFractionalWidth: number;
    setGroupState: SetGroupStateFunction<C>;
    setSortState: SetSortStateFunction<C>;
    setSelectedRowKeys?: SetSelectedRowKeysFunction<C>;
    supportsSelection: boolean;
    size: TableSize;
  }) => {
    // We already know `selectedRowKeys` is a subset of `selectableRowKeys`, so
    // if they are the same size, the sets are equal.
    const allSelected = selectedRowKeys.size === selectableRowKeys.size;

    const onCheckedChange = useCallback(() => {
      // Gmail-style: clear if anything is selected, else select all.
      setSelectedRowKeys?.(prev => new Set(prev.size > 0 ? [] : selectableRowKeys));
    }, [selectableRowKeys, setSelectedRowKeys]);

    return (
      <BaseTableRow>
        {supportsSelection && (
          <BaseTableColumnHeader w="16" p={"0 !important"}>
            <SelectionCheckbox
              checked={allSelected ? true : selectedRowKeys.size > 0 ? "indeterminate" : false}
              onCheckedChange={onCheckedChange}
            />
          </BaseTableColumnHeader>
        )}
        {columnIds.map(columnId => (
          <ColumnHeader
            key={columnId}
            config={config}
            columnId={columnId}
            sortState={sortState}
            setGroupState={setGroupState}
            setSortState={setSortState}
            totalFractionalWidth={totalFractionalWidth}
            size={size}
          />
        ))}
      </BaseTableRow>
    );
  },
);
